import { useState } from 'react';
import { Badge, Button } from '../../ui';
import { formatNumber } from '../shared';
import type { ClusterRead } from '../../../types/clustering';
import styles from './Results.module.css';
import { clusterBriefLink } from '../../../utils/siteContext';

export const SIGNAL_LEGEND = [
  { key: 'opp', label: 'Opp', name: 'Opportunity score' },
  { key: 'cann', label: 'Cann', name: 'Cannibalisation risk' },
  { key: 'gap', label: 'Gap', name: 'Competitor gap score' },
  { key: 'aio', label: 'AIO', name: 'AI Overview share' },
] as const;

type SignalKey = (typeof SIGNAL_LEGEND)[number]['key'];

interface ClusterRowProps {
  cluster: ClusterRead;
  canEdit: boolean;
  isExpanded: boolean;
  isRenaming: boolean;
  onToggle: () => void;
  onRename: (next: string) => void | Promise<void>;
  canUseInBrief?: boolean;
}

function scoreChip(label: string, value: number | null, variant: 'primary' | 'warning' | 'info') {
  if (value === null || value === undefined) return null;
  const display = value <= 1 ? `${Math.round(value * 100)}%` : Math.round(value);
  return (
    <Badge variant={variant} size="sm">
      {label}: {display}
    </Badge>
  );
}

function percent(value: number | null | undefined, scale: 'score' | 'ratio' = 'score') {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const normalized = scale === 'ratio' ? value * 100 : value;
  return Math.max(0, Math.min(100, Math.round(normalized)));
}

function SignalStrip({ cluster }: { cluster: ClusterRead }) {
  const signalValues: Record<SignalKey, number | null> = {
    opp: percent(cluster.opportunity_score),
    cann: percent(cluster.cannibalization_risk, 'ratio'),
    gap: percent(cluster.competitor_gap_score),
    aio: percent(cluster.ai_overview_pct),
  };
  const signalTones: Record<SignalKey, string> = {
    opp: styles.signalOpportunity,
    cann: styles.signalRisk,
    gap: styles.signalGap,
    aio: styles.signalAio,
  };
  const signals = SIGNAL_LEGEND.map((signal) => ({
    ...signal,
    value: signalValues[signal.key],
    tone: signalTones[signal.key],
  }));

  return (
    <div className={styles.signalStrip} aria-label="Cluster signals">
      {signals.map((signal) => {
        const valueText = signal.value === null ? '—' : `${signal.value}%`;
        return (
          <div key={signal.key} className={styles.signalItem} title={`${signal.name}: ${valueText}`}>
            <abbr className={styles.signalLabel} title={signal.name}>
              {signal.label}
            </abbr>
          <span className={styles.signalTrack} aria-hidden="true">
            <span
              className={`${styles.signalFill} ${signal.tone}`}
              style={{ width: `${signal.value ?? 0}%` }}
            />
          </span>
          </div>
        );
      })}
    </div>
  );
}

export function ClusterRow({
  cluster,
  canEdit,
  isExpanded,
  isRenaming,
  onToggle,
  onRename,
  canUseInBrief = false,
}: ClusterRowProps) {
  const [draft, setDraft] = useState(cluster.cluster_label);
  const [editing, setEditing] = useState(false);

  const commit = async () => {
    const next = draft.trim();
    if (next && next !== cluster.cluster_label) {
      await onRename(next);
    }
    setEditing(false);
  };

  return (
    <>
      <tr
        className={styles.row}
        onClick={(e) => {
          if (editing) return;
          if ((e.target as HTMLElement).closest('button, input, a')) return;
          onToggle();
        }}
      >
        <td>
          <div className={styles.nameCell}>
            <span aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
            {editing ? (
              <input
                className={styles.renameInput}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void commit();
                  if (e.key === 'Escape') {
                    setDraft(cluster.cluster_label);
                    setEditing(false);
                  }
                }}
                disabled={isRenaming}
                autoFocus
              />
            ) : (
              <span>{cluster.cluster_label}</span>
            )}
            {canUseInBrief && !cluster.is_merged && <a href={clusterBriefLink(cluster)} onClick={e => e.stopPropagation()}>Use in a brief</a>}
            {canEdit && !editing && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft(cluster.cluster_label);
                  setEditing(true);
                }}
              >
                Rename
              </Button>
            )}
          </div>
        </td>
        <td>{formatNumber(cluster.keywords_count)}</td>
        <td>{formatNumber(cluster.search_volume)}</td>
        <td>{formatNumber(cluster.opportunity_score)}</td>
        <td>{cluster.search_intent ?? '—'}</td>
        <td>{cluster.funnel_stage ?? '—'}</td>
        <td>
          {cluster.is_merged ? (
            <Badge variant="tertiary" size="sm">
              Merged
            </Badge>
          ) : (
            <Badge variant="secondary" size="sm">
              {cluster.cluster_label_source.replace(/_/g, ' ')}
            </Badge>
          )}
        </td>
        <td>
          <SignalStrip cluster={cluster} />
        </td>
      </tr>

      {isExpanded && (
        <tr className={styles.detailRow}>
          <td colSpan={8}>
            <div className={styles.detailGrid}>
              <div>
                <strong>Keywords ({cluster.keywords.length})</strong>
                <ul className={styles.chipList}>
                  {cluster.keywords.slice(0, 30).map((kw) => (
                    <li key={kw}>{kw}</li>
                  ))}
                  {cluster.keywords.length > 30 && (
                    <li>+ {cluster.keywords.length - 30} more</li>
                  )}
                </ul>
              </div>
              <div>
                <strong>Top competitors</strong>
                <ul className={styles.chipList}>
                  {cluster.top_competitors.length === 0 && <li>—</li>}
                  {cluster.top_competitors.map((c) => (
                    <li key={c.domain}>
                      <span>{c.domain}</span>
                      <span>{Math.round(c.pct_of_kws * 100)}% ({c.hits})</span>
                    </li>
                  ))}
                </ul>
                <div className={styles.chipRow}>
                  {scoreChip('Opportunity', cluster.opportunity_score, 'primary')}
                  {scoreChip('Cannibalisation', cluster.cannibalization_risk, 'warning')}
                  {scoreChip('Gap', cluster.competitor_gap_score, 'info')}
                  {scoreChip('AI Overview', cluster.ai_overview_pct, 'info')}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
